@if (isLoading()) {
	<div class="min-h-[70vh] flex items-center justify-center bg-mist">
		<div class="flex flex-col items-center gap-4">
			<div
				class="w-12 h-12 border-3 border-secondary border-t-transparent rounded-full animate-spin"
			></div>
			<p class="text-charcoal/50 text-sm tracking-wide">Loading…</p>
		</div>
	</div>
} @else if (notFound()) {
	<div class="min-h-[70vh] flex items-center justify-center bg-mist px-6">
		<div class="text-center max-w-md">
			<h1 class="font-cenzo text-4xl font-bold text-primary mb-4">Service not found</h1>
			<p class="text-charcoal/60 mb-8">
				This service may have been renamed or is no longer offered.
			</p>
			<button
				(click)="backToServices()"
				class="px-8 py-4 bg-primary text-white rounded-full font-bold tracking-wide hover:bg-primary-75 transition-colors duration-300"
			>
				View all services
			</button>
		</div>
	</div>
} @else if (service(); as svc) {
	<article>
		<!-- ═══ Hero ═══ -->
		<section class="relative min-h-[55vh] md:min-h-[60vh] w-full overflow-hidden flex">
			<div class="absolute inset-0 bg-linear-to-br from-charcoal via-primary to-charcoal"></div>

			@if (svc.image_path) {
				<img
					[src]="getImageUrl(svc.image_path)"
					[alt]="svc.service_name"
					(error)="onImageError($event)"
					class="absolute inset-0 w-full h-full object-cover opacity-25"
				/>
			}

			<div class="absolute inset-0 overflow-hidden pointer-events-none">
				<div
					class="absolute top-1/4 -left-32 w-[500px] h-[500px] bg-secondary/15 rounded-full blur-3xl"
				></div>
				<div
					class="absolute bottom-0 -right-32 w-[600px] h-[600px] bg-secondary/10 rounded-full blur-3xl"
				></div>
			</div>

			<div
				class="relative z-10 w-full max-w-4xl mx-auto flex flex-col justify-center px-6 md:px-16 py-20"
			>
				<!-- Breadcrumb -->
				<nav class="flex items-center gap-2 text-sm text-white/50 mb-8" aria-label="Breadcrumb">
					<a routerLink="/services" class="hover:text-secondary transition-colors"
						>Services</a
					>
					@if (svc.category) {
						<span aria-hidden="true">/</span>
						<span>{{ svc.category.name }}</span>
					}
				</nav>

				<h1
					class="font-cenzo text-4xl md:text-6xl font-bold text-white leading-[1.05] mb-5 text-balance"
				>
					{{ svc.service_name }}
				</h1>

				@if (svc.tagline) {
					<p class="text-secondary text-lg md:text-xl leading-relaxed max-w-2xl mb-10">
						{{ svc.tagline }}
					</p>
				}

				<div>
					<button
						(click)="openBookingModal()"
						class="px-8 py-4 bg-secondary text-primary rounded-full font-bold text-lg tracking-wide shadow-xl hover:shadow-secondary/30 hover:scale-105 transition-all duration-300"
					>
						{{ ctaButtonLabel() }}
					</button>
				</div>
			</div>
		</section>

		<div class="bg-mist">
			<div class="max-w-4xl mx-auto px-6 md:px-16 py-16 md:py-24 flex flex-col gap-16 md:gap-24">
				<!-- ═══ Intro ═══ -->
				@if (svc.intro_paragraphs?.length) {
					<section class="flex flex-col gap-5">
						@for (paragraph of svc.intro_paragraphs; track $index) {
							<p class="text-charcoal/80 text-lg leading-[1.75]">{{ paragraph }}</p>
						}
					</section>
				} @else if (svc.short_description) {
					<section>
						<p class="text-charcoal/80 text-lg leading-[1.75]">
							{{ svc.short_description }}
						</p>
					</section>
				}

				<!-- ═══ Why The OG Hair Studio ═══ -->
				@if (svc.why_us_points?.length) {
					<section>
						<h2 class="section-heading">{{ whyUsTitle() }}</h2>
						<ul class="flex flex-col gap-4">
							@for (point of svc.why_us_points; track $index) {
								<li class="flex gap-4">
									<svg
										class="w-5 h-5 mt-1 shrink-0 text-secondary"
										fill="none"
										stroke="currentColor"
										stroke-width="2.5"
										viewBox="0 0 24 24"
										aria-hidden="true"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											d="M5 13l4 4L19 7"
										/>
									</svg>
									<span class="text-charcoal/80 text-[1.0625rem] leading-relaxed">{{
										point
									}}</span>
								</li>
							}
						</ul>
					</section>
				}

				<!-- ═══ Common Concerns We Address ═══ -->
				@if (svc.concerns?.length) {
					<section>
						<h2 class="section-heading">{{ concernsTitle() }}</h2>
						<div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
							@for (concern of svc.concerns; track $index) {
								<div class="concern-card">
									<h3 class="font-cenzo text-lg font-bold text-primary mb-2">
										{{ concern.title }}
									</h3>
									@if (concern.description) {
										<p class="text-charcoal/65 text-[0.9375rem] leading-relaxed">
											{{ concern.description }}
										</p>
									}
								</div>
							}
						</div>
					</section>
				}

				<!-- ═══ How It Works ═══ -->
				@if (svc.how_it_works?.length) {
					<section>
						<h2 class="section-heading">{{ howItWorksTitle() }}</h2>
						<ol class="flex flex-col">
							@for (step of svc.how_it_works; track $index) {
								<li class="step-row">
									<div class="step-marker">
										<span class="step-number">{{ $index + 1 }}</span>
										@if (!$last) {
											<span class="step-line" aria-hidden="true"></span>
										}
									</div>
									<div class="pb-8">
										<h3 class="font-cenzo text-lg font-bold text-primary mb-1.5">
											{{ step.title }}
										</h3>
										@if (step.description) {
											<p class="text-charcoal/70 leading-relaxed">
												{{ step.description }}
											</p>
										}
									</div>
								</li>
							}
						</ol>
					</section>
				}

				<!-- ═══ Who It's For ═══ -->
				@if (svc.who_its_for) {
					<section>
						<h2 class="section-heading">Who It's For</h2>
						<p class="text-charcoal/80 text-lg leading-[1.75]">{{ svc.who_its_for }}</p>
					</section>
				}

				<!-- ═══ FAQs ═══ -->
				@if (svc.faqs?.length) {
					<section>
						<h2 class="section-heading">Frequently Asked Questions</h2>
						<div class="flex flex-col gap-3">
							@for (faq of svc.faqs; track $index) {
								<div class="faq-item" [class.is-open]="openFaqIndex() === $index">
									<button
										type="button"
										class="faq-question"
										[attr.aria-expanded]="openFaqIndex() === $index"
										(click)="toggleFaq($index)"
									>
										<span>{{ faq.question }}</span>
										<svg
											class="faq-chevron"
											fill="none"
											stroke="currentColor"
											stroke-width="2"
											viewBox="0 0 24 24"
											aria-hidden="true"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												d="M19 9l-7 7-7-7"
											/>
										</svg>
									</button>
									@if (openFaqIndex() === $index) {
										<div class="faq-answer">
											<p>{{ faq.answer }}</p>
										</div>
									}
								</div>
							}
						</div>
					</section>
				}

				<!-- ═══ Related Services ═══ -->
				@if (svc.related_services?.length) {
					<section>
						<h2 class="section-heading">Related Services</h2>
						<div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
							@for (related of svc.related_services; track related.id) {
								<a
									[routerLink]="['/services', related.slug]"
									class="related-card group"
								>
									<span
										class="font-cenzo text-lg font-bold text-primary group-hover:text-secondary transition-colors"
									>
										{{ related.service_name }}
									</span>
									@if (related.short_description) {
										<span
											class="text-charcoal/60 text-[0.9375rem] leading-relaxed"
										>
											{{ related.short_description }}
										</span>
									}
									<span
										class="text-secondary text-sm font-semibold tracking-wide mt-1"
									>
										Read more →
									</span>
								</a>
							}
						</div>
					</section>
				}
			</div>
		</div>

		<!-- ═══ Call to Action ═══ -->
		<section class="relative overflow-hidden bg-linear-to-br from-primary via-charcoal to-primary">
			<div class="absolute inset-0 overflow-hidden pointer-events-none">
				<div
					class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-secondary/10 rounded-full blur-3xl"
				></div>
			</div>
			<div
				class="relative z-10 max-w-3xl mx-auto px-6 md:px-16 py-20 md:py-24 text-center flex flex-col items-center gap-8"
			>
				<p class="text-white text-xl md:text-2xl leading-relaxed text-balance">
					{{
						svc.cta_text ||
							'Ready to find out what would actually work for you? Book a consultation at our Banjara Hills studio.'
					}}
				</p>
				<button
					(click)="openBookingModal()"
					class="px-10 py-5 bg-secondary text-primary rounded-full font-bold text-lg tracking-wide shadow-2xl hover:shadow-secondary/30 hover:scale-105 transition-all duration-300"
				>
					{{ ctaButtonLabel() }}
				</button>
			</div>
		</section>
	</article>
}

@if (showBookingModal()) {
	<app-booking-modal (close)="closeBookingModal()" />
}
